import React, { useState } from "react";
import { auth, googleProvider } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/LoginPage.css";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // 이메일 인증 확인
      if (!userCredential.user.emailVerified) {
        setError("이메일 인증이 완료되지 않았습니다. 이메일을 확인해 주세요.");
        await auth.signOut(); // 인증되지 않은 사용자는 로그아웃 처리
        return;
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("로그인 에러:", error);
      switch (error.code) {
        case "auth/user-not-found":
          setError("등록되지 않은 이메일입니다. 회원가입을 먼저 진행해주세요.");
          break;
        case "auth/wrong-password":
          setError("비밀번호가 일치하지 않습니다. 다시 확인해주세요.");
          break;
        case "auth/invalid-email":
          setError(
            "유효하지 않은 이메일 형식입니다. 이메일을 다시 확인해주세요."
          );
          break;
        case "auth/invalid-credential":
          setError(
            "이메일 또는 비밀번호가 올바르지 않습니다. 다시 확인해주세요."
          );
          break;
        case "auth/too-many-requests":
          setError(
            "너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요."
          );
          break;
        case "auth/user-disabled":
          setError("이 계정은 비활성화되었습니다. 관리자에게 문의하세요.");
          break;
        default:
          setError(`로그인 중 오류가 발생했습니다. (${error.code})`);
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userDocRef = doc(db, "users", result.user.uid);

      // 기존 유저 데이터 확인
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // 새 사용자인 경우에만 초기 데이터 설정
        await setDoc(userDocRef, {
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          // subscription 필드는 아예 설정하지 않음!
        });
      }

      navigate("/dashboard");
    } catch (error) {
      console.error("구글 로그인 에러:", error);
      setError("구글 로그인 중 오류가 발생했습니다.");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMessage("");

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage(
        "비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요."
      );
    } catch (error) {
      console.error("비밀번호 재설정 에러:", error);
      switch (error.code) {
        case "auth/invalid-email":
          setResetMessage("유효하지 않은 이메일 형식입니다.");
          break;
        case "auth/user-not-found":
          setResetMessage("등록되지 않은 이메일입니다.");
          break;
        default:
          setResetMessage(
            `비밀번호 재설정 중 오류가 발생했습니다. (${error.code})`
          );
      }
    }
  };

  const toggleResetForm = () => {
    setShowResetForm(!showResetForm);
    setResetMessage("");
  };

  return (
    <div className="login-page">
      <div className="logo" onClick={() => navigate("/")}>
        TOEICMON
      </div>
      <div className="login-content">
        <div className="login-image-section">
          <img src="/images/login-image.svg" alt="Login" />
        </div>
        <div className="login-form-section">
          <div className="login-container">
            <h2>Login</h2>
            {!showResetForm ? (
              <>
                <form className="login-form" onSubmit={handleSubmit}>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="login-page-input"
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="login-page-input"
                  />
                  {error && <p className="error-message">{error}</p>}
                  <button type="submit" className="login-form-submit-btn">
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="google-login-btn"
                  >
                    <img src="/images/google-icon.svg" alt="Google" />
                    Google로 로그인
                  </button>
                </form>
                <p className="forgot-password">
                  <a href="#" onClick={toggleResetForm}>
                    비밀번호를 잊으셨나요?
                  </a>
                </p>
                <p className="signup-link">
                  계정이 없으신가요? <a href="/signup">회원가입</a>
                </p>
              </>
            ) : (
              <div className="password-reset-form">
                <h3>비밀번호 재설정</h3>
                <p>
                  가입한 이메일 주소를 입력하시면 비밀번호 재설정 링크를
                  보내드립니다.
                </p>
                <form onSubmit={handlePasswordReset}>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="이메일 주소"
                    required
                    className="login-page-input"
                  />
                  {resetMessage && (
                    <p
                      className={
                        resetMessage.includes("발송")
                          ? "success-message"
                          : "error-message"
                      }
                    >
                      {resetMessage}
                    </p>
                  )}
                  <button type="submit" className="login-form-submit-btn">
                    재설정 링크 보내기
                  </button>
                  <button
                    type="button"
                    onClick={toggleResetForm}
                    className="back-to-login-btn"
                  >
                    로그인으로 돌아가기
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
